import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Space,
    Modal,
    Form,
    Input,
    ColorPicker,
    message,
    Popconfirm,
    Tag,
    Row,
    Col,
    Statistic,
    Tooltip,
    Switch,
    Alert
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    TagOutlined,
    ReloadOutlined,
    EyeOutlined
} from '@ant-design/icons';
import useProductCategories from '../../hooks/useProductCategories';

const { TextArea } = Input;

const ProductCategoriesManagementPage = () => {
    const [form] = Form.useForm();
    const [editingCategory, setEditingCategory] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);

    // ใช้ hook สำหรับจัดการ categories พร้อม product count
    const {
        categories,
        loading,
        error,
        loadCategories,
        createCategory,
        updateCategory,
        updateCategoryStatus,
        deleteCategory,
        refresh
    } = useProductCategories({
        includeProductCount: true,
        autoLoad: true
    });

    // โหลดข้อมูลครั้งแรก
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            await loadCategories();
        } catch (error) {
            console.error('Error loading categories:', error);
            message.error('เกิดข้อผิดพลาดในการโหลดข้อมูล');
        }
    };

    // แสดง modal เพิ่มหมวดหมู่ใหม่
    const handleAdd = () => {
        setEditingCategory(null);
        form.resetFields();
        setModalVisible(true);
    };

    // แสดง modal แก้ไขหมวดหมู่
    const handleEdit = (category) => {

        console.log('edit...', category);

        setEditingCategory(category);
        form.setFieldsValue({
            category_name: category.label,
            category_code: category.value,
            color_code: category.color_code,
            description: category.description,
            display_order: category.display_order,
            is_active: category.is_active
        });
        setModalVisible(true);
    };

    // บันทึกหมวดหมู่
    const handleSave = async (values) => {
        setConfirmLoading(true);
        try {
            const categoryData = {
                ...values,
                color_code: values.color_code?.toHexString?.() || values.color_code || '#1890ff'
            };

            if (editingCategory) {
                // แก้ไขหมวดหมู่
                await updateCategory(editingCategory.id, categoryData);
                message.success('แก้ไขหมวดหมู่เรียบร้อยแล้ว');
            } else {
                // เพิ่มหมวดหมู่ใหม่
                await createCategory(categoryData);
                message.success('เพิ่มหมวดหมู่เรียบร้อยแล้ว');
            }

            setModalVisible(false);
            form.resetFields();
            setEditingCategory(null);
            await refresh(); // รีเฟรชข้อมูล
        } catch (error) {
            console.error('Error saving category:', error);
            message.error('เกิดข้อผิดพลาดในการบันทึกข้อมูล');
        } finally {
            setConfirmLoading(false);
        }
    };

    // ลบหมวดหมู่
    const handleDelete = async (categoryId) => {
        try {
            await deleteCategory(categoryId);
            message.success('ลบหมวดหมู่เรียบร้อยแล้ว');
            await refresh(); // รีเฟรชข้อมูล
        } catch (error) {
            console.error('Error deleting category:', error);
            if (error.response?.data?.message?.includes('มีสินค้า')) {
                message.error('ไม่สามารถลบหมวดหมู่นี้ได้เนื่องจากมีสินค้าอยู่');
            } else {
                message.error('เกิดข้อผิดพลาดในการลบข้อมูล');
            }
        }
    };

    // สลับสถานะ (เปิด/ปิด)
    const handleToggleStatus = async (category, newStatus) => {
        try {
            await updateCategoryStatus(category.id, { is_active: newStatus });
            message.success(newStatus ? 'เปิดใช้งานหมวดหมู่เรียบร้อยแล้ว' : 'ปิดใช้งานหมวดหมู่เรียบร้อยแล้ว');
            await refresh();
        } catch (error) {
            console.error('Error toggling status:', error);
            message.error('เกิดข้อผิดพลาดในการเปลี่ยนสถานะ');
        }
    };

    // คำนวณสถิติ
    const totalCategories = categories.length;
    const activeCategories = categories.filter(cat => cat.is_active).length;
    const inactiveCategories = totalCategories - activeCategories;
    const categoriesWithProducts = categories.filter(cat => cat.product_count > 0).length;

    // กำหนด columns สำหรับตาราง
    const columns = [
        {
            title: 'ลำดับ',
            dataIndex: 'display_order',
            key: 'display_order',
            width: 80,
        },
        {
            title: 'หมวดหมู่',
            key: 'category_info',
            width: 250,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Space>
                        <Tag
                            color={record.color_code}
                            style={{ fontSize: '12px', margin: 0 }}
                        >
                            <TagOutlined /> {record.label}
                        </Tag>
                        <Tag color={record.is_active ? 'green' : 'red'} size="small">
                            {record.is_active ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                        </Tag>
                    </Space>
                    <span style={{ fontSize: '12px', color: '#666' }}>
                        รหัส: {record.value}
                    </span>
                </Space>
            )
        },
        {
            title: 'คำอธิบาย',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (text) => text || '-'
        },
        {
            title: 'จำนวนสินค้า',
            key: 'product_count',
            width: 120,
            align: 'center',
            render: (_, record) => (
                <Tag color={record.product_count > 0 ? 'blue' : 'default'}>
                    {record.product_count} รายการ
                </Tag>
            )
        },
        {
            title: 'สถานะ',
            key: 'is_active',
            width: 120,
            render: (value, record) => (
                <Space direction="vertical" size={0}>

                    <Switch
                        size="small"
                        checked={value}
                        onChange={(checked) => handleToggleStatus(record, checked)}
                        checkedChildren="เปิด"
                        unCheckedChildren="ปิด"
                    />

                </Space>
            )
        },
        {
            title: 'จัดการ',
            key: 'actions',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title="แก้ไข">
                        <Button
                            type="primary"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                            disabled={record.product_count > 0}
                        />
                    </Tooltip>
                    <Tooltip title="ลบ">
                        <Popconfirm
                            title="คุณแน่ใจหรือไม่ที่จะลบหมวดหมู่นี้?"
                            description={
                                record.product_count > 0
                                    ? `หมวดหมู่นี้มีสินค้าอยู่ ${record.product_count} รายการ การลบจะไม่สามารถทำได้`
                                    : 'การลบจะไม่สามารถย้อนกลับได้'
                            }
                            onConfirm={() => handleDelete(record.id)}
                            disabled={record.product_count > 0}
                            okButtonProps={{
                                danger: true,
                                disabled: record.product_count > 0
                            }}
                        >
                            <Button
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                                disabled={record.product_count > 0}
                            />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            )
        }
    ];

    // ถ้า error ให้แสดงข้อความ
    if (error) {
        return (
            <Card>
                <Alert
                    message="เกิดข้อผิดพลาด"
                    description={error}
                    type="error"
                    showIcon
                    action={
                        <Button size="small" danger onClick={loadData}>
                            ลองอีกครั้ง
                        </Button>
                    }
                />
            </Card>
        );
    }

    return (
        <div>
            {/* หัวหน้าหน้า */}
            <div style={{ marginBottom: 24 }}>
                <h1>จัดการหมวดหมู่สินค้า</h1>
                <p style={{ color: '#666', marginTop: 8 }}>
                    จัดการหมวดหมู่สำหรับการจำแนกประเภทสินค้าในระบบ
                </p>
            </div>

            {/* สถิติ */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="หมวดหมู่ทั้งหมด"
                            value={totalCategories}
                            prefix={<TagOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="เปิดใช้งาน"
                            value={activeCategories}
                            valueStyle={{ color: '#3f8600' }}
                            prefix={<TagOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="ปิดใช้งาน"
                            value={inactiveCategories}
                            valueStyle={{ color: '#cf1322' }}
                            prefix={<TagOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="มีสินค้า"
                            value={categoriesWithProducts}
                            valueStyle={{ color: '#1890ff' }}
                            prefix={<TagOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            {/* เมนูและปุ่มต่างๆ */}
            <Card style={{ marginBottom: 16 }}>
                <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                        {/* <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAdd}
                        >
                            เพิ่มหมวดหมู่ใหม่
                        </Button> */}
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={loadData}
                            loading={loading}
                        >
                            รีเฟรช
                        </Button>
                    </Space>
                </Space>

                {/* ตาราง */}
                <Table
                    columns={columns}
                    dataSource={categories}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        total: categories.length,
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `ทั้งหมด ${total} รายการ`,
                    }}
                    scroll={{ x: 800 }}
                />
            </Card>

            {/* Modal เพิ่ม/แก้ไขหมวดหมู่ */}
            <Modal
                title={editingCategory ? 'แก้ไขหมวดหมู่' : 'เพิ่มหมวดหมู่ใหม่'}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                    setEditingCategory(null);
                }}
                onOk={() => form.submit()}
                confirmLoading={confirmLoading}
                width={600}
                okText="บันทึก"
                cancelText="ยกเลิก"
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSave}
                    disabled={confirmLoading}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="ชื่อหมวดหมู่"
                                name="category_name"
                                rules={[
                                    { required: true, message: 'กรุณาระบุชื่อหมวดหมู่' },
                                    { max: 100, message: 'ชื่อหมวดหมู่ต้องไม่เกิน 100 ตัวอักษร' }
                                ]}
                            >
                                <Input placeholder="เช่น อุปกรณ์คอมพิวเตอร์" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="รหัสหมวดหมู่"
                                name="category_code"
                                rules={[
                                    { required: true, message: 'กรุณาระบุรหัสหมวดหมู่' },
                                    { pattern: /^[a-z0-9_]+$/, message: 'รหัสต้องเป็นตัวพิมพ์เล็ก ตัวเลข และขีดล่างเท่านั้น' },
                                    { max: 50, message: 'รหัสต้องไม่เกิน 50 ตัวอักษร' }
                                ]}
                            >
                                <Input
                                    placeholder="เช่น computer_hardware"
                                    disabled={!!editingCategory} // ไม่ให้แก้รหัสเมื่อแก้ไข
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="สี"
                                name="color_code"
                                rules={[{ required: false, message: 'กรุณาเลือกสี' }]}
                            >
                                <ColorPicker
                                    showText
                                    placeholder="เลือกสี"
                                    format="hex"
                                />
                            </Form.Item>
                        </Col>
                        {/* <Col span={12}>
              <Form.Item
                label="ลำดับการแสดง"
                name="display_order"
                rules={[
                  { type: 'number', min: 0, message: 'ลำดับต้องเป็นตัวเลขบวก' }
                ]}
              >
                <Input 
                  type="number" 
                  placeholder="เช่น 1" 
                  min={0}
                />
              </Form.Item>
            </Col> */}
                    </Row>

                    <Form.Item
                        label="คำอธิบาย"
                        name="description"
                        rules={[
                            { max: 500, message: 'คำอธิบายต้องไม่เกิน 500 ตัวอักษร' }
                        ]}
                    >
                        <TextArea
                            rows={3}
                            placeholder="รายละเอียดเกี่ยวกับหมวดหมู่นี้ (ไม่บังคับ)"
                        />
                    </Form.Item>

                    {!editingCategory && (
                        <Form.Item
                            label="สถานะการใช้งาน"
                            name="is_active"
                            valuePropName="checked"
                            initialValue={true}
                        >
                            <Switch
                                checkedChildren="เปิดใช้งาน"
                                unCheckedChildren="ปิดใช้งาน"
                                defaultChecked
                            />
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </div>
    );
};

export default ProductCategoriesManagementPage;